import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLoaderData, useNavigate } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LockResetIcon from "@mui/icons-material/LockReset";
import LoginIcon from "@mui/icons-material/Login";
import SyncIcon from "@mui/icons-material/Sync";

import { impersonateUser } from "../../../../application/actions/authActions";
import UserManagementService from "../../../../infrastructure/api/userManagement";
import { resolvePostLoginUrl } from "../../../../infrastructure/utils/authSession";
import { isAdminUser } from "../../../utils/authRoleHelpers";
import useCheckPermission from "../../../common/hooks/useCheckPermission";
import DeleteConfirm from "../../../components/shared/Dialog/DeleteConfirm";
import BreadcrumbsComponent from "../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../components/shared/table";
import { toast } from "react-toastify";

const UserInfoPage = () => {
  const roleInfoList = useLoaderData("root");
  const dispatch = useDispatch();
  let navigate = useNavigate();
  const [impersonatingId, setImpersonatingId] = useState(null);
  const canEdit = useCheckPermission("edit");
  const canImpersonate = isAdminUser() && canEdit;
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    data: {},
  });
  const [passwordDialog, setPasswordDialog] = useState({
    open: false,
    data: {},
    newPassword: "",
    confirmPassword: "",
    loading: false,
  });
  const headCells = [
    {
      id: "updateOn",
      type: "date",
      disablePadding: true,
      label: "تاریخ بروزرسانی",
    },
    {
      id: "userId",
      disablePadding: false,
      label: "ایدی",
    },
    {
      id: "firstName",
      disablePadding: false,
      label: "نام",
    },
    {
      id: "lastName",
      disablePadding: false,
      label: "نام خانوادگی",
    },
    {
      id: "mobile",
      disablePadding: false,
      label: "شماره همراه",
    },
    {
      id: "roleName",
      disablePadding: false,
      label: "گروه کاربری",
    },
    {
      id: "userName",
      disablePadding: false,
      label: "نام کاربری",
    },
    {
      id: "userKind",
      disablePadding: false,
      label: "نوع کابر",
    },
    {
      id: "isActive",
      type: "boolean",
      disablePadding: false,
      label: "وضعیت",
    },
  ];

  const onEditClicked = (row) => {
    navigate(`${row.userId}`);
  };

  const onDeleteClicked = (row) => {
    setDeleteConfirm((old) => ({ ...old, open: true, data: row }));
  };

  const confirmedDeleteUser = () => {
    return UserManagementService.deleteUserInfoApi({
      id: deleteConfirm.data.userId,
    });
  };

  const onChangePasswordClicked = (row) => {
    setPasswordDialog({
      open: true,
      data: row,
      newPassword: "",
      confirmPassword: "",
      loading: false,
    });
  };

  const handleClosePasswordDialog = (reload) => {
    setPasswordDialog((old) => ({
      ...old,
      open: false,
      data: {},
      newPassword: "",
      confirmPassword: "",
      loading: false,
    }));
    if (reload) {
      navigate();
    }
  };

  const handleSubmitPasswordChange = async () => {
    const { data, newPassword, confirmPassword } = passwordDialog;

    if (!newPassword || !confirmPassword) {
      toast.error("لطفاً کلمه عبور جدید و تکرار آن را وارد کنید.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("کلمه عبور و تکرار آن یکسان نیست.");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("کلمه عبور باید حداقل ۶ کاراکتر باشد.");
      return;
    }

    try {
      setPasswordDialog((old) => ({ ...old, loading: true }));
      const res = await UserManagementService.changeUserPasswordApi({
        id: data.userId,
        newPassword,
      });

      if (res?.isSuccess) {
        toast.success(res.message || "کلمه عبور با موفقیت تغییر کرد.");
        handleClosePasswordDialog(false);
      } else {
        toast.error(res?.message || "تغییر کلمه عبور با خطا مواجه شد.");
        setPasswordDialog((old) => ({ ...old, loading: false }));
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "تغییر کلمه عبور با خطا مواجه شد."
      );
      setPasswordDialog((old) => ({ ...old, loading: false }));
    }
  };

  const onFetchData = () => {
    navigate();
  };

  const onImpersonateClicked = async (row) => {
    if (!isAdminUser()) {
      toast.error("فقط مدیر سیستم می‌تواند وارد حساب کاربران شود.");
      return;
    }

    const displayName = `${row.firstName || ""} ${row.lastName || ""}`.trim();
    const confirmed = window.confirm(
      `آیا می‌خواهید وارد حساب «${displayName || row.userName}» شوید؟`
    );
    if (!confirmed) {
      return;
    }

    try {
      setImpersonatingId(row.userId);
      const result = await dispatch(impersonateUser(row.userId)).unwrap();
      toast.success("ورود به حساب کاربر انجام شد.");
      navigate(resolvePostLoginUrl("/", result.user), { replace: true });
    } catch (error) {
      toast.error(
        typeof error === "string" ? error : "ورود به حساب کاربر ناموفق بود."
      );
    } finally {
      setImpersonatingId(null);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />

        <DeleteConfirm
          message={`برای حذف کاربر "${deleteConfirm.data.firstName} ${deleteConfirm.data.lastName}" اطمینان دارید؟`}
          open={deleteConfirm.open}
          onClose={(loadAgain) => {
            setDeleteConfirm({
              open: false,
              data: {},
            });
            if (loadAgain) {
              navigate();
            }
          }}
          onSubmit={confirmedDeleteUser}
        />

        <Dialog
          open={passwordDialog.open}
          onClose={() => handleClosePasswordDialog(false)}
        >
          <DialogTitle>
            تغییر کلمه عبور کاربر{" "}
            {passwordDialog.data?.firstName} {passwordDialog.data?.lastName}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1, minWidth: 320 }}>
              <TextField
                label="کلمه عبور جدید"
                type="password"
                fullWidth
                margin="normal"
                value={passwordDialog.newPassword}
                onChange={(e) =>
                  setPasswordDialog((old) => ({
                    ...old,
                    newPassword: e.target.value,
                  }))
                }
              />
              <TextField
                label="تکرار کلمه عبور جدید"
                type="password"
                fullWidth
                margin="normal"
                value={passwordDialog.confirmPassword}
                onChange={(e) =>
                  setPasswordDialog((old) => ({
                    ...old,
                    confirmPassword: e.target.value,
                  }))
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handleClosePasswordDialog(false)}>
              انصراف
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={handleSubmitPasswordChange}
              disabled={passwordDialog.loading}
            >
              {passwordDialog.loading ? "در حال ثبت..." : "ثبت"}
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title="کاربران"
            action={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "fit-content",
                }}
              >
                {useCheckPermission("create") && (
                  <Link to={"create"}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                    >
                      ایجاد
                    </Button>
                  </Link>
                )}

                <Divider
                  orientation="vertical"
                  variant="middle"
                  flexItem
                  sx={{ mx: 1 }}
                />

                <IconButton
                  variant="text"
                  color="primary"
                  onClick={onFetchData}
                >
                  <SyncIcon />
                </IconButton>
              </Box>
            }
          />

          <CardContent>
            <Grid container>
              <Grid item xs={12} md={12}>
                <EnhancedTable
                  rowKey={"userId"}
                  rows={roleInfoList}
                  headCells={headCells}
                  pageSize={100}
                  actions={[
                    {
                      icon: useCheckPermission("edit") ? (
                        <EditIcon sx={{ width: "1rem", height: "1rem" }} />
                      ) : null,
                      onClick: onEditClicked,
                      color: "primary",
                    },
                    {
                      icon: useCheckPermission("delete") ? (
                        <DeleteIcon sx={{ width: "1rem", height: "1rem" }} />
                      ) : null,
                      onClick: onDeleteClicked,
                      color: "secondary",
                    },
                    {
                      icon: useCheckPermission("edit") ? (
                        <LockResetIcon
                          sx={{ width: "1rem", height: "1rem" }}
                        />
                      ) : null,
                      onClick: onChangePasswordClicked,
                      color: "info",
                    },
                    {
                      icon: canImpersonate ? (
                        <LoginIcon sx={{ width: "1rem", height: "1rem" }} />
                      ) : null,
                      onClick: (row) => {
                        if (impersonatingId) return;
                        onImpersonateClicked(row);
                      },
                      color: "warning",
                      tooltip: "ورود به حساب کاربر",
                    },
                  ]}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default UserInfoPage;
